<template>
  <div class="app-pdf-viewer app-pdf-viewer--simple" :class="{ 'app-pdf-viewer--loading': loading }">
    <div v-if="loading" class="app-pdf-viewer__loading">
      <div class="app-loading-spinner"></div>
      <p>{{ $t('common.loading') }}</p>
    </div>
    
    <div v-else-if="error" class="app-pdf-viewer__error">
      <i class="fas fa-exclamation-triangle"></i>
      <p>{{ error }}</p>
      <slot name="error-actions"></slot>
    </div>      <div v-else class="app-pdf-viewer__container">
      <iframe 
        :src="pdfUrl + '#toolbar=1&navpanes=0&scrollbar=0&page=1&view=FitH'" 
        class="app-pdf-viewer__iframe" 
        frameborder="0" 
        scrolling="no"
        allowfullscreen
      ></iframe>
    </div>
  </div>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref } from 'vue';
// @ts-ignore
import { watch, onMounted } from 'vue';

interface Props {
  pdfUrl: string;
}

const props = defineProps<Props>();

const loading = ref<boolean>(true);
const error = ref<string | null>(null);

// Function to validate URL
const validateUrl = (url: string): boolean => {
  if (!url) {
    console.error('PDF URL is empty or undefined');
    error.value = 'No PDF URL provided';
    return false;
  }

  try {
    new URL(url); // This will throw if URL is invalid
    return true;
  } catch (e) {
    console.error('Invalid PDF URL:', e);
    error.value = 'Invalid PDF URL';
    return false;
  }
};

watch(() => props.pdfUrl, (newUrl: string) => {
  if (newUrl) {
    console.log('PDF URL changed:', newUrl);
    if (validateUrl(newUrl)) {
      loading.value = false;
      error.value = null;
    } else {
      loading.value = false;
    }
  } else {
    loading.value = true;
    error.value = 'No PDF URL provided';
  }
}, { immediate: true });

onMounted(() => {
  if (props.pdfUrl) {
    loading.value = false;
  } else {
    loading.value = true;
  }
});
</script>

<style scoped>
.app-pdf-viewer {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--neutral-100);
  border-radius: var(--border-radius);
  position: relative;
  overflow: visible;
}

.app-pdf-viewer__loading,
.app-pdf-viewer__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-xl);
  height: 100%;
  min-height: 300px;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--neutral-100);
}

.app-loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--neutral-200);
  border-left-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: var(--space-md);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.app-pdf-viewer__error i {
  font-size: 3rem;
  color: var(--danger);
  margin-bottom: var(--space-md);
}

.app-pdf-viewer__container {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 800px;
  overflow: visible;
}

.app-pdf-viewer__iframe {
  width: 100%;
  min-height: 800px;
  border: none;
  overflow: hidden;
  pointer-events: auto;
}

@media (max-width: 1024px) {
  .app-pdf-viewer__container,
  .app-pdf-viewer__iframe {
    min-height: 700px;
  }
}

@media (max-width: 768px) {
  .app-pdf-viewer__container,
  .app-pdf-viewer__iframe {
    min-height: 600px;
  }
}

@media (max-width: 480px) {
  .app-pdf-viewer__container,
  .app-pdf-viewer__iframe {
    min-height: 500px;
  }
}
</style>
